import { StandardEvents } from '@shopify/events';

class MvurraCartNotification extends HTMLElement {
  #controller;
  #revision = 0;
  #positionFrame = 0;
  #returnFocus;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    document.addEventListener('mvurra:quickshop-open', this.#dismiss, { signal });
    document.addEventListener('theme-drawer:open', this.#dismiss, { signal });
    document.addEventListener('pointerdown', this.#onOutside, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    window.addEventListener('resize', this.#queuePosition, { signal, passive: true });
    window.addEventListener('scroll', this.#queuePosition, { signal, passive: true });
    window.addEventListener('pageshow', this.#dismiss, { signal });
    this.querySelector('[data-notification-close]')?.addEventListener('click', () => this.#close(true), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#positionFrame);
    this.#revision += 1;
  }

  #onCartUpdate = (event) => {
    if (event.action !== 'add' || !event.promise) return;
    this.#close();
    const revision = ++this.#revision;
    const source = event.target;
    const lines = event.lines || [];
    event.promise.then(({ detail }) => {
      if (!this.isConnected || revision !== this.#revision || detail?.didError) return;
      const items = Array.isArray(detail?.items) ? detail.items : [];
      const added = lines.flatMap((line) => {
        const variantId = String(line.merchandiseId || '').split('/').pop();
        const item = items.find((candidate) => String(candidate.variant_id || candidate.id) === variantId);
        const quantity = Number(line.quantity);
        return item && quantity > 0 ? [{ item, quantity }] : [];
      });
      if (!added.length) return;
      this.#render(added);
      requestAnimationFrame(() => {
        if (!this.isConnected || revision !== this.#revision) return;
        this.#returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : source;
        this.hidden = false;
        this.#position();
        this.querySelector('[data-notification-status]').textContent = `${this.dataset.addedLabel} ${added.map(({ item, quantity }) => `${item.product_title || item.title}, ${this.dataset.quantityLabel}: ${quantity}`).join('. ')}`;
      });
    }).catch(() => {});
  };

  #render(added) {
    const fragment = document.createDocumentFragment();
    const currency = this.dataset.currency || 'USD';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency });
    for (const { item, quantity } of added) {
      const row = document.createElement('article');
      row.className = 'mv-cart-notification__item';
      const imageLink = document.createElement('a');
      imageLink.href = item.url;
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      const imageUrl = item.featured_image?.url || item.image;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.width = 100;
        image.height = 125;
        image.className = 'mv-cart-notification__image';
        imageLink.append(image);
      }
      const info = document.createElement('div');
      const name = document.createElement('a');
      name.className = 'mv-cart-notification__name';
      name.href = item.url;
      name.textContent = item.product_title || item.title;
      const options = document.createElement('dl');
      options.className = 'mv-cart-notification__options';
      const values = (item.options_with_values || []).filter((option) => option.value !== 'Default Title');
      values.push({ name: this.dataset.quantityLabel, value: quantity });
      for (const option of values) {
        const key = document.createElement('dt');
        key.textContent = `${option.name}:`;
        const value = document.createElement('dd');
        value.textContent = option.value;
        options.append(key, value);
      }
      const price = document.createElement('p');
      price.className = 'mv-cart-notification__price';
      price.textContent = money.format(((item.final_price ?? item.price) * quantity) / 100);
      info.append(name, options, price);
      row.append(imageLink, info);
      fragment.append(row);
    }
    this.querySelector('[data-notification-items]').replaceChildren(fragment);
  }

  #queuePosition = () => {
    if (this.hidden || this.#positionFrame) return;
    this.#positionFrame = requestAnimationFrame(() => {
      this.#positionFrame = 0;
      this.#position();
    });
  };

  #position() {
    const anchor = document.querySelector('header-actions [aria-controls="cart-drawer"], header-actions .action__cart');
    const rect = anchor?.getBoundingClientRect();
    const gutter = window.innerWidth <= 480 ? 12 : 16;
    const width = Math.min(375, window.innerWidth - gutter * 2);
    const left = Math.max(gutter, Math.min((rect?.right || window.innerWidth - gutter) + 20 - width, window.innerWidth - width - gutter));
    const top = Math.max(16, Math.min((rect?.bottom || 60) + 14, window.innerHeight - 180));
    this.style.setProperty('--mv-notification-left', `${left}px`);
    this.style.setProperty('--mv-notification-top', `${top}px`);
    this.style.setProperty('--mv-notification-pointer', `${Math.max(20, Math.min((rect ? rect.left + rect.width / 2 : window.innerWidth - 40) - left - 7, width - 30))}px`);
  }

  #close(restoreFocus = false) {
    if (this.hidden) return;
    const hadFocus = this.contains(document.activeElement);
    this.hidden = true;
    this.querySelector('[data-notification-status]').textContent = '';
    if (restoreFocus && hadFocus && this.#returnFocus?.isConnected) this.#returnFocus.focus({ preventScroll: true });
  }

  #dismiss = () => this.#close();
  #onOutside = (event) => {
    if (!this.hidden && !this.contains(event.target)) this.#close();
  };
  #onKeydown = (event) => {
    if (event.key === 'Escape' && !this.hidden) this.#close(true);
  };
}

if (!customElements.get('mvurra-cart-notification')) customElements.define('mvurra-cart-notification', MvurraCartNotification);
